import React, { ReactElement } from 'react'
import { Alert, Snackbar, TextField, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { AccreditationStatus } from './shared-types'

// Map accreditation status to MUI chip color
export let getStatusColor = (
  status?: AccreditationStatus,
): 'default' | 'info' | 'success' | 'error' => {
  if (status === 'Draft') return 'default'
  if (status === 'Submitted') return 'info'
  if (status === 'Approved') return 'success'
  return 'error'
}

// Common form field props type
export type FormFieldProps<T = string> = {
  field: {
    name: string
    state: {
      value: T
      meta: {
        errors: string[]
      }
    }
    handleChange: (value: T) => void
    handleBlur: () => void
  }
}

// Shared NotesField component
export let NotesField = ({ field }: FormFieldProps): ReactElement => (
  <TextField
    label="Notes"
    name={field.name}
    value={field.state.value}
    onChange={(e) => field.handleChange(e.target.value)}
    onBlur={field.handleBlur}
    multiline
    rows={3}
    fullWidth
    sx={{ bgcolor: 'background.paperDark' }}
    placeholder="Add any notes or comments about this pending accreditation"
    error={field.state.meta.errors.length > 0}
    helperText={field.state.meta.errors.join(', ')}
    aria-describedby={
      field.state.meta.errors.length > 0 ? `${field.name}-error` : undefined
    }
  />
)

// Shared Rejected Alert component
export let RejectedAlert = ({
  rejectedDate,
  notes,
}: {
  rejectedDate?: string | null
  notes?: string | null
}): ReactElement | null => {
  if (!rejectedDate) return null

  return (
    <Alert severity="error" sx={{ mx: 2, mb: 2 }}>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
        }}
      >
        Rejected on {dayjs(rejectedDate).format('MMM D, YYYY')}
      </Typography>
      {notes && (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          {notes}
        </Typography>
      )}
    </Alert>
  )
}

// Shared Approved Alert component
export let ApprovedAlert = ({
  approvedDate,
}: {
  approvedDate?: string | null
}): ReactElement | null => {
  if (!approvedDate) return null

  return (
    <Alert severity="success" sx={{ mx: 2, mb: 2 }}>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
        }}
      >
        Approved on {dayjs(approvedDate).format('MMM D, YYYY')}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        Ready to finalize this accreditation update.
      </Typography>
    </Alert>
  )
}

// Shared operation snackbars
export let OperationSnackbars = ({
  successMessage,
  errorMessage,
  onClear,
}: {
  successMessage?: string | null
  errorMessage?: string | null
  onClear: () => void
}): ReactElement => (
  <>
    <Snackbar
      open={!!successMessage}
      autoHideDuration={3000}
      onClose={onClear}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      aria-live="polite"
    >
      <Alert
        onClose={onClear}
        severity="success"
        variant="filled"
        role="status"
      >
        {successMessage}
      </Alert>
    </Snackbar>

    <Snackbar
      open={!!errorMessage}
      autoHideDuration={5000}
      onClose={onClear}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      aria-live="assertive"
    >
      <Alert onClose={onClear} severity="error" variant="filled" role="alert">
        {errorMessage}
      </Alert>
    </Snackbar>
  </>
)

// Common dialog paper props
export let dialogPaperProps = {
  sx: {
    bgcolor: 'background.default',
    borderRadius: 2,
  },
}

// Helper to calculate boolean flags for accreditation state
export let getAccreditationFlags = <F, P>(finalized?: F, pending?: P) => ({
  hasNoAccreditation: !finalized && !pending,
  hasOnlyFinalized: !!finalized && !pending,
  hasOnlyPending: !finalized && !!pending,
  hasBoth: !!finalized && !!pending,
})

// Helper to calculate changes between finalized and pending accreditations
export let calculateFieldChanges = <
  F extends Record<string, any>,
  P extends Record<string, any>,
>(
  finalized: F | undefined,
  pending: P | undefined,
  fieldMappings: { key: string; label: string }[],
): string[] => {
  if (!finalized || !pending) return []

  return fieldMappings
    .filter(({ key }) => {
      let pendingValue = pending[key]
      let finalizedValue = finalized[key]
      return !!pendingValue && pendingValue !== finalizedValue
    })
    .map(({ label }) => label)
}

// Factory for creating handler functions
export let createAccreditationHandlers = <P extends { id: string }>(
  pending: P | undefined,
  eventId: string,
  dispatch: any,
  actions: {
    update: any
    finalize: any
  },
) => ({
  handleSubmitForApproval: () => {
    if (!pending?.id) return
    dispatch(
      actions.update({
        id: pending.id,
        eventId,
        status: 'Submitted' as const,
      }),
    )
  },
  handleApprove: () => {
    if (!pending?.id) return
    dispatch(
      actions.update({
        id: pending.id,
        eventId,
        status: 'Approved' as const,
      }),
    )
  },
  handleFinalize: () => {
    if (!pending?.id) return
    dispatch(
      actions.finalize({
        id: pending.id,
        eventId,
      }),
    )
  },
})
